import type { AttendeeInformation, Place } from 'src/app/interfaces/interfaces';

export type MentionKind = 'attendee' | 'resource';

export type MentionSuggestion = {
  kind: MentionKind;
  trackId: string;
  label: string;
  subtitle: string;
  icon: string;
  value: string;
};

export type MessageSegment =
  | { type: 'text'; value: string }
  | {
      type: 'mention';
      kind: MentionKind;
      label: string;
      icon: string;
      value: string;
    };

function formatAttendeeLabel(attendee: AttendeeInformation): string {
  const name = `${attendee.firstName ?? ''} ${attendee.lastName ?? ''}`.trim();
  return name || attendee.userPrincipalName;
}

function getPlaceMentionIcon(place: Place): string {
  if (place.type === 'room') {
    return 'business-outline';
  }
  if (place.type === 'workspace' && place.capacity === 1) {
    return 'laptop-outline';
  }
  if (place.type === 'workspace') {
    return 'briefcase-outline';
  }
  if (place.tag?.toLowerCase() === 'parking') {
    return 'car-outline';
  }
  return 'location-outline';
}

/** Build @-picker suggestions from attendees and places. */
export function buildMentionSuggestions(
  query: string,
  attendees: AttendeeInformation[],
  places: Place[],
  findAttendee: (attendee: AttendeeInformation, searchTerm: string) => boolean,
  findPlace: (place: Place, searchTerm: string) => boolean,
  limit = 8,
  options: { allowResource?: boolean } = {},
): MentionSuggestion[] {
  const allowResource = options.allowResource ?? true;
  const perGroup = Math.max(4, Math.ceil(limit / 2));
  const suggestions: MentionSuggestion[] = [];

  for (const attendee of attendees) {
    if (!findAttendee(attendee, query)) {
      continue;
    }

    suggestions.push({
      kind: 'attendee',
      trackId: `attendee:${attendee.userPrincipalName}`,
      label: formatAttendeeLabel(attendee),
      subtitle: attendee.userPrincipalName,
      icon: 'person-circle-outline',
      value: attendee.userPrincipalName,
    });

    if (
      suggestions.filter((item) => item.kind === 'attendee').length >= perGroup
    ) {
      break;
    }
  }

  if (allowResource) {
    for (const place of places) {
      if (!findPlace(place, query)) {
        continue;
      }

      suggestions.push({
        kind: 'resource',
        trackId: `resource:${place.emailAddress}`,
        label: place.displayName,
        subtitle: place.emailAddress,
        icon: getPlaceMentionIcon(place),
        value: place.emailAddress,
      });

      if (
        suggestions.filter((item) => item.kind === 'resource').length >=
        perGroup
      ) {
        break;
      }
    }
  }

  return suggestions.slice(0, limit);
}

export function textAlreadyHasPlaceEmail(
  text: string,
  places: Place[],
): boolean {
  const lower = text.toLowerCase();
  return places.some((place) =>
    lower.includes(place.emailAddress.toLowerCase()),
  );
}

type MentionLookup = {
  value: string;
  kind: MentionKind;
  label: string;
  icon: string;
};

function buildMentionLookups(
  attendees: AttendeeInformation[],
  places: Place[],
): MentionLookup[] {
  const lookups: MentionLookup[] = [
    ...attendees.map((attendee) => ({
      value: attendee.userPrincipalName,
      kind: 'attendee' as const,
      label: formatAttendeeLabel(attendee),
      icon: 'person-circle-outline',
    })),
    ...places.map((place) => ({
      value: place.emailAddress,
      kind: 'resource' as const,
      label: place.displayName,
      icon: getPlaceMentionIcon(place),
    })),
  ];

  // Prefer longer emails first so partial overlaps don't win.
  return lookups.sort((a, b) => b.value.length - a.value.length);
}

function isBoundary(char: string | undefined): boolean {
  return char == null || /\s|,|;/.test(char);
}

/**
 * Split message text into plain text and mention chips by matching known
 * attendee / place emails already present in the text.
 */
export function parseMessageSegments(
  text: string,
  attendees: AttendeeInformation[],
  places: Place[],
): MessageSegment[] {
  if (!text) {
    return [];
  }

  const lookups = buildMentionLookups(attendees, places);
  if (lookups.length === 0) {
    return [{ type: 'text', value: text }];
  }

  const segments: MessageSegment[] = [];
  let index = 0;

  while (index < text.length) {
    let matched: MentionLookup | null = null;

    for (const lookup of lookups) {
      const end = index + lookup.value.length;
      if (end > text.length) {
        continue;
      }

      const slice = text.slice(index, end);
      if (slice.toLowerCase() !== lookup.value.toLowerCase()) {
        continue;
      }

      if (
        !isBoundary(text[index - 1]) ||
        !isBoundary(text[end])
      ) {
        continue;
      }

      matched = lookup;
      break;
    }

    if (matched) {
      segments.push({
        type: 'mention',
        kind: matched.kind,
        label: matched.label,
        icon: matched.icon,
        value: matched.value,
      });
      index += matched.value.length;
      continue;
    }

    const value = text[index];
    const last = segments[segments.length - 1];
    if (last?.type === 'text') {
      last.value += value;
    } else {
      segments.push({ type: 'text', value });
    }
    index += 1;
  }

  return segments;
}
